import { useState, useEffect } from "react"
import clsx from "clsx"
import ReactStars from "react-stars"
import { useRouter } from "next/router"
import { toast } from "react-toastify"
import { useTranslation } from "i18n"
import metadata from "metadata.json"

// Material UI
import { makeStyles, fade, lighten } from "@material-ui/core/styles"
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Divider,
  CircularProgress
} from "@material-ui/core"

// Components
import Button from "components/shared/Button"
import StoreAppBar from "components/store/StoreAppBar"
import KnowledgeGainedCard from "components/store/shared/KnowledgeGainedCard"
import SkillsObtainedCard from "components/store/shared/SkillsObtainedCard"
import KeyHighlightsList from "components/store/shared/KeyHighlightsList"
import AboutItem from "components/store/shared/AboutItem"
// import ItemCertificate from "components/store/shared/ItemCertificate"
import WhyChooseItem from "components/store/shared/WhyChooseItem"
import CourseItem from "components/store/shared/CourseItem"
import FAQ from "components/store/shared/FAQ"
import LearningModes from "components/store/shared/LearningModes"
import Footer from "components/landing_page/Footer"
import LoadingScreen from "components/globals/LoadingScreen"

// Icons
import PlayArrowIcon from "@material-ui/icons/PlayArrow"

// Helpers
import axios from "shared/axios"
import urls from "shared/urls"
import {
  getAuthInfo,
  getPluralOrSingular,
  getMinimumPrice,
  handleSSO,
  getPlatformURL,
  getTrackingDetails
} from "shared/helpers"
import { addToCartV2 } from "shared/common_apis"
import { CERTIFICATION_SLUGS } from "shared/constants"
import Metadata from "shared/metadata"
import { GA, GA_EVENTS } from "utils/googleAnalytics"

const useStyles = makeStyles(theme => ({
  contentPadding: {
    paddingLeft: theme.spacing(20),
    paddingRight: theme.spacing(20),
    [theme.breakpoints.down("md")]: {
      paddingLeft: theme.spacing(7),
      paddingRight: theme.spacing(7)
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5)
    },
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    },
    transition: theme.transitions.create("padding", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  banner: {
    padding: theme.spacing(13, 20),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(13, 7)
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 5)
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(7, 2)
    },

    backgroundColor: fade(theme.palette.common.black, 0.8),
    color: theme.palette.common.white
  },

  iconButtonRoot: {
    marginRight: theme.spacing(2),
    padding: theme.spacing(0.8),
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    "&:hover": {
      backgroundColor: fade(theme.palette.common.white, 0.8)
    }
  },
  navItem: {
    padding: theme.spacing(2.5, 4),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 2)
    }
  },
  divider: {
    backgroundColor: theme.palette.common.white
  },
  tabActive: {
    borderBottom: `4px solid ${theme.palette.primary.main}`
  },

  // Buttons
  enrollBtn: {
    backgroundImage: theme.gradients.primaryButton,
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 8),
    boxShadow: theme.shadows[25],
    "&:disabled": {
      color: theme.palette.common.white
    }
  },
  // downloadBrochureBtn: {
  //   padding: theme.spacing(1, 5)
  // },

  // Background Colors/Gradients
  whyChooseItem: {
    backgroundImage: theme.gradients.bluePurple,
    color: theme.palette.common.white,
    opacity: 0.9
  },
  certificateBackground: {
    backgroundColor: lighten(theme.palette.primary.main, 0.88)
  },
  targetAudienceBackground: {
    color: theme.palette.common.white,
    backgroundColor: lighten(theme.palette.primary.main, 0.2)
  },
  footer: {
    backgroundColor: theme.palette.grey.aluminium,
    [theme.breakpoints.down("sm")]: {
      marginBottom: "10vh"
    }
  },

  // Helper Classes
  textWhite: {
    color: theme.palette.common.white
  }
}))

// Tab Choices
const ABOUT_TAB = "ABOUT_TAB"
const SYLLABUS_TAB = "SYLLABUS_TAB"
const WHY_THIS_CERTIFICATION_TAB = "WHY_THIS_CERTIFICATION_TAB"
const REVIEWS_TAB = "REVIEWS_TAB"
const FAQ_TAB = "FAQ_TAB"

const COMPARISON_TABLE = "compareLearningModes"

const navTabs = [
  {
    display: "About",
    id: ABOUT_TAB
  },
  {
    display: "Syllabus",
    id: SYLLABUS_TAB
  },
  {
    display: "Why?",
    id: WHY_THIS_CERTIFICATION_TAB
  },
  {
    display: "Reviews",
    id: REVIEWS_TAB
  },
  {
    display: "FAQ",
    id: FAQ_TAB
  }
]

const mpdFaqs = [
  {
    question:
      "Will I receive any certification after completing a course from Suraasa?",
    answer:
      "Yes. You are awarded a certificate of achievement upon completion of any course from Suraasa. These certificates can be accessed and downloaded from the Suraasa portal throughout your lifetime."
  },
  {
    question:
      "How do I receive the KHDA certificate after completing the 6 courses?",
    answer:
      "When you complete the 6 courses, and receive the 6 portal certificates, send us an email at: care@suraasa.com so that we apply for your KHDA certificate."
  },
  {
    question: "Does the package include the KHDA certificate fee?",
    answer:
      "Yes, the package includes both course and KHDA certificate fees. No extra payment is needed."
  },
  {
    question:
      "What modes of payment can I use to enroll in this certification?",
    answer:
      "Suraasa accepts major modes of payment such as Debit Cards, Credit Cards, American Express Cards, and Wire Transfer. You can contact Suraasa Care for offline payments."
  },
  {
    question: "Can I access the course contents more than once?",
    answer:
      "Yes. You can access the course contents via Suraasa Web Portal or Mobile Applications as many times as you want within 12 months of enrolling into the certification."
  },
  {
    question: "How long do I have access to Suraasa to complete the courses?",
    answer:
      "All courses have a valid license for 12 months. If you sign up for courses in promotional packages, the deadline to complete them all remains 12 months."
  },
  {
    question:
      "What happens if I do not complete the certification within 12 months?",
    answer:
      "If you do not complete the certification within 12 months, you can ask for renewal of access for upto 6 months at a fee of $20 per month for each renewed course."
  }
]

export default function CertificationDetails(props) {
  const { query, asPath } = useRouter()
  const certificationSlug = query.certificationSlug
  const { slug } = props

  const { t } = useTranslation()
  const classes = useStyles()
  const [buyLoading, setBuyLoading] = useState(false)
  const [userInfo, setUserInfo] = useState(null)

  // This state is here to prevent ReactStars from behaving abnormally.
  // The component was not rendering half stars until it re-rendered once.
  const [renderRatingStars, setRenderRatingStars] = useState(false)

  const [certification, setCertification] = useState(null)
  const [certificationItems, setCertificationItems] = useState([])
  // const [reviews, setReviews] = useState([])

  const [tab, setTab] = useState("")

  const [taxesIncluded, setTaxesIncluded] = useState(false)

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const getCertificationDetail = () => {
      axios
        .get(urls.store.certifications.details(certificationSlug))
        .then(response => {
          GA.trackEvent(GA_EVENTS.view_item, {
            items: [
              {
                item_id: response.data.data.uuid,
                item_category: "certification",
                item_name: response.data.data.name
              }
            ]
          })
          setCertification(response.data.data)
          setTaxesIncluded(
            getMinimumPrice(response.data.data.products).inclusive_of_tax
          )
          setLoading(false)
        })
        .catch(console.error)
    }

    const getCertificationItems = () => {
      axios
        .get(urls.store.certifications.items(certificationSlug), {
          params: {
            fields: ["description"]
          }
        })
        .then(response => {
          setCertificationItems(response.data.data)
        })
        .catch(console.error)
    }

    // const getReviews = () => {
    //   axios
    //     .get(urls.store.certifications.reviews(certificationSlug))
    //     .then(response => {
    //       setReviews(response.data.data.data)
    //     })
    // }

    setRenderRatingStars(true)

    if (certificationSlug) {
      setUserInfo(getAuthInfo())

      getCertificationDetail()
      getCertificationItems()
    }
  }, [certificationSlug])

  const handleEnrollmentClick = () => {
    const el = document.getElementById(COMPARISON_TABLE)
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" })
    } else {
      handlePurchase()
    }
  }

  const handlePurchase = productId => {
    if (userInfo === null) {
      handleSSO()
      return
    }

    const { products } = certification
    productId = productId || products[0].id
    setBuyLoading(true)

    addToCartV2(productId, products[0].centres)
      .then(() => {
        setBuyLoading(false)
        toast.success("Added to cart")

        GA.trackEvent(
          GA_EVENTS.add_to_cart,
          getTrackingDetails(certification, {
            type: "certification",
            productId
          })
        )
        window.location = getPlatformURL("learn", "/orders/payment/")
      })
      .catch(error => {
        if (error.response) {
          const response = error.response.data
          if (response.errors) {
            toast.error(response.errors.message)
          }
        }
        setBuyLoading(false)
      })
  }

  const changeTab = tab => {
    document.getElementById(tab).scrollIntoView()
    setTab(tab)
  }

  const formatPrice = products => {
    const { price, mrp, currency } = getMinimumPrice(products)

    if (mrp === price) {
      return (
        <span>
          {currency.symbol}
          {price}
        </span>
      )
    }
    return (
      <Box component="span" mr={1}>
        <s>
          {currency.symbol}
          {mrp}
        </s>
        <Box component="span" ml={1}>
          {currency.symbol}
          {price}
        </Box>
      </Box>
    )
  }

  const getMetaData = () => {
    const data = metadata.certificationDetails[slug]
    return {
      ...data,
      title: data?.title || certification?.name,
      description:
        data?.description || certification?.certification_attributes.tagline
    }
  }

  return (
    <>
      <Metadata data={getMetaData()}>
        {certification && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                image: certification.image ?? "",
                url: `https://www.suraasa.com/store${asPath}`,
                name: certification.name,
                description: certification.description ?? "",
                sku: certification.uuid,
                brand: {
                  "@type": "Brand",
                  name: "Suraasa"
                },
                offers: {
                  "@type": "Offer",
                  availability: "https://schema.org/InStock",
                  priceCurrency: `${
                    getMinimumPrice(certification.products).currency.code
                  }`,
                  price: `${getMinimumPrice(certification.products).price}`
                }
              })
            }}
          />
        )}
      </Metadata>
      {!certificationSlug === CERTIFICATION_SLUGS.mpd && <StoreAppBar />}
      {certificationSlug === CERTIFICATION_SLUGS.mpd && (
        <>
          <StoreAppBar position="fixed" />
        </>
      )}
      {loading ? (
        <LoadingScreen />
      ) : (
        <div>
          <Grid container direction="column">
            <Grid
              id={ABOUT_TAB}
              item
              style={{
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundImage: `url(${certification.banner_image})`
              }}
            >
              <div className={clsx(classes.banner, classes.contentPadding)}>
                <Grid container spacing={3} direction="column">
                  <Grid item md={12}>
                    <Typography
                      variant="h3"
                      className="text-medium"
                      component="h1"
                    >
                      {certification.name}
                    </Typography>
                    <Typography variant="h5" component="h2">
                      {certification.certification_attributes.tagline}
                    </Typography>
                  </Grid>

                  {/* {certification.rating !== 0 && ( */}
                  <Grid item>
                    <div className="flex align-items-center">
                      {renderRatingStars && (
                        <ReactStars
                          count={5}
                          edit={false}
                          value={4.5}
                          size={30}
                          // value={certification.rating}
                        />
                      )}
                      <Box pt={1} ml={1}>
                        <Typography variant="h6">
                          {getPluralOrSingular("Star", 4.8)}
                        </Typography>
                      </Box>
                    </div>
                  </Grid>
                  {/* )} */}

                  {certification.products ? (
                    <Grid item>
                      <Box py={2}>
                        <Grid container spacing={1} alignItems="flex-end">
                          {certification.products.length > 1 && (
                            <Grid item>
                              <Typography variant="h5">
                                {t("Starting")}:
                              </Typography>
                            </Grid>
                          )}

                          <Grid item>
                            <Typography variant="h4">
                              {formatPrice(certification.products)}
                            </Typography>
                          </Grid>
                          {taxesIncluded ? (
                            <Typography variant="body1">
                              {t("(Including Taxes)")}
                            </Typography>
                          ) : (
                            <Typography variant="body1">
                              {t("(Excluding Taxes)")}
                            </Typography>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  ) : (
                    <Box my={2} />
                  )}

                  <Grid item>
                    <Grid container spacing={4} alignItems="center">
                      <Grid item>
                        <Grid container direction="column" alignItems="center">
                          <Grid item>
                            <Button
                              variant="contained"
                              fullWidth
                              disabled={
                                certification.is_enrolled ||
                                !certification.products ||
                                buyLoading
                              }
                              onClick={handleEnrollmentClick}
                              className={classes.enrollBtn}
                            >
                              <Typography variant="body1">
                                {certification.is_enrolled
                                  ? t("Enrolled")
                                  : certification.products
                                  ? t("Enroll Now")
                                  : t("Not Available")}
                              </Typography>
                              {buyLoading && (
                                <CircularProgress
                                  color="white"
                                  size="20px"
                                  style={{
                                    marginLeft: "12px"
                                  }}
                                />
                              )}
                            </Button>
                          </Grid>
                          {certification.number_of_enrollments > 300 && (
                            <Grid item>
                              <Typography variant="body2">
                                {getPluralOrSingular(
                                  "Learner",
                                  certification.number_of_enrollments
                                )}
                                &nbsp;Enrolled
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      {(certification.is_enrolled ||
                        certification.is_previewable) && (
                        <Grid item>
                          <Box mb={{ sm: 2 }}>
                            <IconButton
                              size="small"
                              classes={{
                                root: classes.iconButtonRoot
                              }}
                              onClick={() => changeTab(SYLLABUS_TAB)}
                            >
                              <PlayArrowIcon fontSize="large" color="primary" />
                            </IconButton>

                            <Typography variant="body1" display="inline">
                              {certification.is_enrolled
                                ? "Continue Learning"
                                : "Demo this certification"}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </Grid>
            <Grid item>
              <Box bgcolor="white" className={classes.contentPadding}>
                <Grid container>
                  {navTabs.map(item => {
                    // Don't render reviews tab if there are no course reviews
                    if (item.id === REVIEWS_TAB) return null
                    return (
                      <Grid
                        item
                        key={item.id}
                        className={clsx(classes.navItem, {
                          [classes.tabActive]: tab === item.id
                        })}
                      >
                        <Typography
                          variant="body1"
                          className="cursor-pointer text-bold"
                          color={tab === item.id ? "primary" : "inherit"}
                          onClick={() => changeTab(item.id)}
                        >
                          {item.display}
                        </Typography>
                      </Grid>
                    )
                  })}
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box my={5} className={classes.contentPadding}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Grid container direction="column" spacing={3}>
                  <Grid item container direction="column">
                    <Box mb={2}>
                      <Grid item>
                        <KnowledgeGainedCard
                          items={
                            certification.certification_attributes
                              .marketing_learning_outcomes
                          }
                        />
                      </Grid>
                    </Box>

                    <Grid item>
                      <SkillsObtainedCard
                        items={certification.certification_attributes.skills}
                      />
                    </Grid>
                  </Grid>
                  <Grid item>
                    <AboutItem
                      type="certification"
                      description={certification.description}
                      images={{
                        awarded_by: certification.awarding_bodies || [],
                        approved_by: certification.approving_bodies || [],
                        accredited_by: certification.accrediting_bodies || []
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <KeyHighlightsList
                  type="certification"
                  prices={certification.products}
                  duration={certification.duration}
                  available_countries={certification.available_countries}
                  language={certification.available_languages}
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            py={7}
            className={clsx(
              classes.targetAudienceBackground,
              classes.textWhite
            )}
          >
            <Box className={classes.contentPadding}>
              <Grid container direction="column">
                <Grid item>
                  <Box mb={4}>
                    <Typography variant="h4">
                      For whom is this certification most useful?
                    </Typography>
                    <Typography variant="h6">
                      While a teacher in any part of the world can undertake
                      this certification, it is best suited for:
                    </Typography>
                  </Box>
                </Grid>
                <Grid item container spacing={4}>
                  {certification.certification_attributes.target_audience &&
                    certification.certification_attributes.target_audience.map(
                      value => (
                        <Grid
                          item
                          container
                          direction="column"
                          spacing={1}
                          xs={12}
                          sm={6}
                          key={value.heading}
                        >
                          <Grid item>
                            <Typography variant="h6">
                              {value.heading}
                            </Typography>
                            <Divider className={classes.divider} />
                          </Grid>
                          <Grid item>
                            <Typography variant="body1">
                              {value.description}
                            </Typography>
                          </Grid>
                        </Grid>
                      )
                    )}
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box py={7} id={SYLLABUS_TAB} className={classes.contentPadding}>
            <Grid container direction="column" spacing={3}>
              <Grid item container alignItems="center">
                <Grid item xs={12} sm>
                  <Grid container direction="column" spacing={1}>
                    <Grid item>
                      <Typography variant="h4">
                        {t("Included Courses & Studies")}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="h6">
                        Following are the 6 courses mandated by The Knowledge
                        and Human Development Authority (KHDA) Dubai for all
                        teachers in Dubai which are included in this package.
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item container direction="column">
                {certificationItems.map((value, index) => (
                  <Grid item key={index}>
                    <Box my={1}>
                      <CourseItem
                        type="course"
                        uuid={value.uuid}
                        slug={value.slug}
                        name={value.name}
                        image={value.image}
                        description={value.description}
                        isActive={value.is_active}
                        isPurchased={value.is_enrolled}
                        allowDemo={value.is_previewable}
                        // Icons
                        rating={value.rating}
                        level={value.level}
                        number_of_enrollments={value.number_of_enrollments}
                        prices={value.prices}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Box>

          <Box
            py={7}
            id={WHY_THIS_CERTIFICATION_TAB}
            className={classes.whyChooseItem}
          >
            <Box className={classes.contentPadding}>
              <WhyChooseItem
                type="certification"
                points={certification.certification_attributes.key_attractions}
                name={certification.name}
              />
            </Box>
          </Box>

          {/* <Box py={7} className={classes.certificateBackground}>
        <Box className={classes.contentPadding}>
          <ItemCertificate
            img="/assets/images/CertificationCertificateTemplate.png"
            handleClick={handleEnrollmentClick}
            is_enrolled={certification.is_enrolled}
            number_of_enrollments={certification.number_of_enrollments}
          />
        </Box>
      </Box> */}

          <Box id={FAQ_TAB} py={7} className={classes.contentPadding}>
            <FAQ
              faqs={
                certification.slug === CERTIFICATION_SLUGS.mpd
                  ? mpdFaqs
                  : undefined
              }
            />
          </Box>

          {certification.products.length > 1 && (
            <LearningModes
              prices={certification.products}
              features={
                certification.certification_attributes.learning_mode_features
              }
              handlePurchase={handlePurchase}
              isEnrolled={certification.is_enrolled}
              buyLoading={buyLoading}
            />
          )}
        </div>
      )}

      <Footer />
    </>
  )
}

export async function getServerSideProps(context) {
  return {
    props: { slug: context.query.certificationSlug }
  }
}
